import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from 'lib/utils';

type ViewOnlyBadgeProps = {
  title?: string;
  description?: string;
  className?: string;
  /** A small chip (like the visibility badge) with the description as its tooltip. */
  compact?: boolean;
};

/**
 * Yellow warning callout for read-only / non-owner views.
 *
 * @example
 * {!canEdit ? <ViewOnlyBadge /> : null}
 * <ViewOnlyBadge description="You cannot edit this tool." />
 * <ViewOnlyBadge compact />
 */
export function ViewOnlyBadge({
  title,
  description,
  className,
  compact = false,
}: ViewOnlyBadgeProps) {
  const { t } = useTranslation();

  if (compact) {
    return (
      <span
        role="status"
        title={description}
        className={cn(
          'inline-flex shrink-0 items-center gap-1 rounded-md border border-[#fde68a] bg-[#fffbeb] px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-[#b45309] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300',
          className
        )}
      >
        <TriangleAlert className="size-3 shrink-0" aria-hidden />
        {title ?? t('SHARE_VIEW_ONLY', { defaultValue: 'View only' })}
      </span>
    );
  }

  return (
    <div
      className={cn(
        'w-full shrink-0 rounded-lg border border-amber-400 bg-amber-50 p-3 transition-all sm:max-w-[280px] dark:border-amber-500/50 dark:bg-amber-950/30',
        className
      )}
      role="status"
    >
      <div className="flex items-center gap-1">
        <TriangleAlert
          className="size-4 shrink-0 text-amber-700 dark:text-amber-400"
          aria-hidden
        />
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
          {title ?? (t('VIEW_ONLY') || 'View only.')}
        </p>
      </div>
      <p className="mt-0.5 text-xs font-normal text-amber-700 dark:text-amber-400/90">
        {description ??
          (t('YOU_ARE_NOT_THE_OWNER_OF_THIS_RESOURCE') ||
            'You are not the owner of this resource.')}
      </p>
    </div>
  );
}

export type { ViewOnlyBadgeProps };
